import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { Search, Pause, Play, X } from 'lucide-react';
import { format } from 'date-fns';

export const Circles = () => {
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    fetchCircles();
  }, []);

  const fetchCircles = async () => {
    try {
      const response = await api.get('/admin/circles');
      setCircles(response.data.data?.circles || response.data.data || []);
    } catch (error) {
      console.error(error);
      // Fallback
      setCircles([
        { id: '1', name: 'Family Vacation Fund', type: 'FAMILY', status: 'ACTIVE', monthlyContribution: 500, createdAt: new Date().toISOString(), memberCount: 4 },
        { id: '2', name: 'College Savings', type: 'FRIENDS', status: 'PENDING', monthlyContribution: 200, createdAt: new Date(Date.now() - 86400000).toISOString(), memberCount: 2 },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleCircleStatus = async (id, status) => {
    const verbs = { PAUSED: 'freeze', CANCELLED: 'cancel', ACTIVE: 'reactivate' };
    let reason = null;
    if (status !== 'ACTIVE') {
      reason = window.prompt(`Reason to ${verbs[status]} this circle (optional, logged for audit):`);
      if (reason === null) return;
    } else {
      if (!window.confirm('Reactivate this circle? Auto-debit will resume on the next cycle.')) return;
    }
    try {
      await api.put(`/admin/circles/${id}/status`, { status, reason });
      fetchCircles();
    } catch (err) {
      alert(`Failed: ${err.response?.data?.error?.message || err.message}`);
    }
  };

  return (
    <div>
      <h1 className="page-title">Circles Management</h1>

      <div className="glass">
        <div className="card-header">
          <div className="action-bar" style={{ margin: 0 }}>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--text-secondary)' }} />
                <input type="text" placeholder="Search circles..." className="search-input" style={{ paddingLeft: 40 }} value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="dropdown" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
              <option value="">All Types</option>
              <option value="FAMILY">Family</option>
              <option value="FRIENDS">Friends</option>
              <option value="WORK">Work</option>
              <option value="BUSINESS">Business</option>
            </select>
          </div>
        </div>

        <div className="table-container" style={{ border: 'none', borderRadius: '0 0 16px 16px' }}>
          {loading ? (
            <div className="loader-container"><div className="spinner"></div></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Circle Name</th>
                  <th>Type</th>
                  <th>Members</th>
                  <th>Monthly Contribution</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {circles
                  .filter(c => !search || c.name?.toLowerCase().includes(search.toLowerCase()))
                  .filter(c => !typeFilter || c.type === typeFilter)
                  .map(circle => (
                  <tr key={circle.id || circle._id}>
                    <td style={{ fontWeight: 500 }}>
                      <Link to={`/circles/${circle.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>{circle.name}</Link>
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>{circle.type}</td>
                    <td>{circle.memberCount || circle.members?.length || 0}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>${circle.monthlyContribution}</td>
                    <td>
                      <span className={`badge ${(circle.status || 'pending').toLowerCase()}`}>
                        {circle.status || 'PENDING'}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>
                      {circle.createdAt ? format(new Date(circle.createdAt), 'MMM dd, yyyy') : 'N/A'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {circle.status === 'ACTIVE' && (
                          <button className="btn-secondary" title="Freeze (pause auto-debit)" style={{ padding: '6px 10px' }}
                                  onClick={() => handleCircleStatus(circle.id, 'PAUSED')}>
                            <Pause size={14} />
                          </button>
                        )}
                        {circle.status === 'PAUSED' && (
                          <button className="btn-success" title="Resume" style={{ padding: '6px 10px' }}
                                  onClick={() => handleCircleStatus(circle.id, 'ACTIVE')}>
                            <Play size={14} />
                          </button>
                        )}
                        {circle.status !== 'CANCELLED' && circle.status !== 'COMPLETED' && (
                          <button className="btn-danger" title="Cancel" style={{ padding: '6px 10px' }}
                                  onClick={() => handleCircleStatus(circle.id, 'CANCELLED')}>
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {circles.filter(c => !search || c.name?.toLowerCase().includes(search.toLowerCase())).filter(c => !typeFilter || c.type === typeFilter).length === 0 && (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '32px', color: 'var(--text-secondary)' }}>
                      No circles found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
